import moment from 'moment';

// Structured Resume Database error codes returned by BevNET.API. The SPA branches
// on these rather than parsing human-readable messages.
export type ResumeDatabaseErrorCode =
  | 'resume_database_not_eligible'
  | 'resume_database_terms_required'
  | 'resume_database_suspended'
  | 'resume_database_rate_limited'
  | 'resume_database_busy'
  | 'resume_contact_quota_exhausted'
  | 'resume_contact_cooldown';

export interface IResumeDatabaseError {
  code: ResumeDatabaseErrorCode | null;
  status: number | undefined;
  message: string | null;
  // Populated for resume_contact_quota_exhausted: when the rolling listing
  // allowance frees up again.
  resetDate: string | null;
  // Populated for resume_contact_cooldown: when this employer/candidate pair may
  // be contacted again.
  nextEligibleDate: string | null;
  // Populated for resume_database_rate_limited / resume_database_busy. The API
  // also sends Retry-After, which fetch does not expose to the SPA cross-origin.
  retryAfterSeconds: number | null;
}

const KNOWN_CODES: ResumeDatabaseErrorCode[] = [
  'resume_database_not_eligible',
  'resume_database_terms_required',
  'resume_database_suspended',
  'resume_database_rate_limited',
  'resume_database_busy',
  'resume_contact_quota_exhausted',
  'resume_contact_cooldown',
];

const readString = (source: Record<string, unknown>, key: string): string | null => {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const readNumber = (source: Record<string, unknown>, key: string): number | null => {
  const value = source[key];
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && isFinite(Number(value))) return Number(value);
  return null;
};

// RTK Query surfaces a JSON error body on `error.data`, but a non-JSON body
// arrives as a PARSING_ERROR string instead. Read both shapes.
const readBody = (error: unknown): Record<string, unknown> => {
  if (!error || typeof error !== 'object') return {};
  const { data } = error as { data?: unknown };
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  if (typeof data === 'string' && data.trim()) return { code: data.trim() };
  return {};
};

export const parseResumeDatabaseError = (error: unknown): IResumeDatabaseError => {
  const body = readBody(error);
  const rawStatus = error && typeof error === 'object'
    ? (error as { status?: number | string; originalStatus?: number })
    : {};
  const status = typeof rawStatus.status === 'number'
    ? rawStatus.status
    : rawStatus.originalStatus;
  const rawCode = readString(body, 'code') || readString(body, 'error');
  const code = rawCode && KNOWN_CODES.indexOf(rawCode as ResumeDatabaseErrorCode) >= 0
    ? rawCode as ResumeDatabaseErrorCode
    : null;

  return {
    code,
    status,
    message: readString(body, 'message'),
    resetDate: readString(body, 'resetDate'),
    nextEligibleDate: readString(body, 'nextEligibleDate'),
    retryAfterSeconds: readNumber(body, 'retryAfterSeconds'),
  };
};

const formatMoment = (value: string | null): string | null => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY [at] h:mm A') : null;
};

const formatRetryAfter = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  if (seconds < 90) return `${Math.ceil(seconds)} seconds`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 90) return `${minutes} minutes`;
  return `${Math.ceil(minutes / 60)} hours`;
};

// One user-facing sentence per structured code. Returns null for errors that are
// not Resume Database policy failures so the caller can fall back to its own copy.
export const getResumeDatabaseErrorMessage = (error: unknown): string | null => {
  const parsed = parseResumeDatabaseError(error);
  if (!parsed.code) return null;

  switch (parsed.code) {
    case 'resume_database_not_eligible':
      return 'An active Featured listing is required to search the Talent Network.';
    case 'resume_database_terms_required':
      return 'Accept the current Resume Database terms to continue.';
    case 'resume_database_suspended':
      return 'Resume Database access for this account is paused. Contact BevNET support to review it.';
    case 'resume_database_rate_limited': {
      const wait = formatRetryAfter(parsed.retryAfterSeconds);
      return wait
        ? `You have reached the Resume Database usage limit. Try again in about ${wait}. Your filters are still here.`
        : 'You have reached the Resume Database usage limit. Try again shortly. Your filters are still here.';
    }
    case 'resume_database_busy':
      return 'The Resume Database is busy right now. Try that again in a moment.';
    case 'resume_contact_quota_exhausted': {
      const reset = formatMoment(parsed.resetDate);
      return reset
        ? `This listing has used all of its introductions for now. The shared allowance resets ${reset}.`
        : 'This listing has used all of its introductions for now. Choose another eligible listing or wait for the allowance to reset.';
    }
    case 'resume_contact_cooldown': {
      const next = formatMoment(parsed.nextEligibleDate);
      return next
        ? `You cannot request another introduction to this candidate until ${next}.`
        : 'You cannot request another introduction to this candidate yet.';
    }
    default:
      return parsed.message;
  }
};
